import { useState } from 'react';
import { Plus, Trash2, ArrowRightLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { TodoListLinkResponse } from '@/lib/api';

interface LinkedListsManagerProps {
  links: TodoListLinkResponse[];
  /** All other lists in the workspace (already excludes the current list). */
  otherLists: Array<{ listId: string; name: string }>;
  onCreate: (targetListId: string) => Promise<unknown>;
  onDelete: (linkId: string) => Promise<unknown>;
}

/**
 * Manage a list's cross-list move shortcuts from List Settings: link this list
 * to other lists in the workspace so "Move to list…" offers them as one-tap
 * targets. A shortcut is a convenience, not a permission — moving is still
 * owner/admin. Add/remove apply immediately (independent of the form's Save).
 */
export function LinkedListsManager({
  links,
  otherLists,
  onCreate,
  onDelete,
}: LinkedListsManagerProps) {
  const [selected, setSelected] = useState<string>('');
  const [busy, setBusy] = useState(false);

  const nameById = new Map(otherLists.map((l) => [l.listId, l.name]));
  const linkedTargetIds = new Set(links.map((l) => l.targetListId));
  // Only offer lists that aren't linked yet.
  const available = otherLists.filter((l) => !linkedTargetIds.has(l.listId));

  const handleAdd = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await onCreate(selected);
      setSelected('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-1.5">
        <ArrowRightLeft className="h-3.5 w-3.5" />
        Linked lists
      </Label>
      <p className="text-xs text-muted-foreground">
        Link this list to others in the workspace so &ldquo;Move to list&rdquo; offers them as quick
        targets. Linking doesn&rsquo;t grant access — moving a task or section is owner/admin.
      </p>

      <div className="space-y-1.5">
        {links.map((link) => (
          <div key={link.linkId} className="flex items-center gap-2">
            <span className="flex-1 truncate text-sm">
              {nameById.get(link.targetListId) ?? 'Unknown list'}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 flex-shrink-0 text-muted-foreground hover:text-destructive"
              aria-label={`Unlink "${nameById.get(link.targetListId) ?? 'list'}"`}
              onClick={() => void onDelete(link.linkId)}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        ))}
        {links.length === 0 && (
          <p className="text-xs text-muted-foreground/70">No linked lists yet.</p>
        )}
      </div>

      {available.length > 0 ? (
        <div className="flex items-center gap-2 pt-1">
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="h-8 flex-1" aria-label="Choose a list to link">
              <SelectValue placeholder="Link another list…" />
            </SelectTrigger>
            <SelectContent>
              {available.map((l) => (
                <SelectItem key={l.listId} value={l.listId}>
                  {l.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="flex-shrink-0"
            onClick={handleAdd}
            disabled={busy || !selected}
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            Link
          </Button>
        </div>
      ) : (
        otherLists.length === 0 && (
          <p className="text-xs text-muted-foreground/70">
            No other lists in this workspace to link to.
          </p>
        )
      )}
    </div>
  );
}
